<template>
  <div class="page container-fluid p-0">
    <FullScreenLoader v-if="loading" />
    <main v-show="!loading" class="cart-content-wrapper">
    <div v-if="items.length === 0" class="empty-cart-message text-center py-5">
      <i class="fa-solid fa-cart-shopping fa-2x text-muted mb-3 d-block"></i>
      <p class="text-muted mb-3">Seu carrinho está vazio! Adicione presentes para finalizar sua compra.</p>
      <router-link class="empty-state-link" :to="`/event/${eventId}`">
        Adicionar presentes
      </router-link>
    </div>

    <div v-else>
      <CartHeader :event="event" />

      <!-- Cabeçalho da seção -->
      <div class="gifts-section-header">
        <div>
          <h2 class="gifts-title">Seus presentes</h2>
          <p class="gifts-subtitle">Revise os itens antes de finalizar a compra</p>
        </div>
        <span class="gifts-count">{{ items.length }} {{ items.length === 1 ? 'item' : 'itens' }}</span>
      </div>

      <!-- Lista de itens -->
      <div class="cart-items-list">
        <div v-for="item in items" :key="item.id" class="cart-item-card card border-0 rounded-4 shadow-sm mb-3">
          <div class="d-flex align-items-center gap-3 p-3 cart-item-row">
            <div class="cart-item-img-wrapper">
              <img :src="item.photo_url" class="cart-item-img" :alt="item.name" />
            </div>
            <div class="flex-grow-1 d-flex flex-column cart-item-details">
              <h5 class="cart-item-title one-line mb-1">{{ item.name }}</h5>
              <p class="cart-item-price fw-bold mb-2">R$ {{ formatCurrency(item.price) }}</p>
              <div class="d-flex align-items-center gap-2 cart-item-actions">
                <div class="cart-qty-pill">
                  <button class="qty-btn" @click="decrease(item)">-</button>
                  <span class="qty-value">{{ item.quantity }}</span>
                  <button class="qty-btn" @click="increase(item)">+</button>
                </div>
                <button class="btn-remove-item" @click="askRemoveItem(item)">
                  <i class="fa-solid fa-trash-can"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </main>

    <footer v-if="items.length >= 1 && !loading" class="app-footer">
      <div class="app-footer__inner">
        <div v-if="isEventDraft" class="draft-checkout-notice">
          <i class="fa-solid fa-lock draft-checkout-notice__icon"></i>
          <span>Este evento está em rascunho. <router-link to="/my-account/profile?ref=createaccount" class="draft-checkout-notice__link">Conclua seu cadastro</router-link> para liberar os pagamentos.</span>
        </div>
        <div class="app-footer__actions">
        <router-link
          class="btn btn-footer-back"
          :to="`/event/${eventId}`"
        >
          <i class="fa-solid fa-plus me-2"></i> Presentes
        </router-link>

        <router-link
          v-if="!isEventDraft"
          class="btn btn-footer-next"
          :to="`/checkout/${eventId}`"
        >
          Pagar R$ {{ formatCurrency(total) }}
          <i class="fa-solid fa-arrow-right ms-2"></i>
        </router-link>
        <button
          v-else
          class="btn btn-footer-next btn-footer-next--disabled"
          disabled
        >
          <i class="fa-solid fa-lock me-2"></i> Pagamento indisponível
        </button>
        </div>
      </div>
    </footer>

    <BottomModal v-if="showRemoveModal" @close="cancelRemove">
      <template #default>
        <div class="confirmation-content p-3">
          <h5 class="text-center mb-2 text-primary-emphasis">Remover presente?</h5>
          <p class="text-muted text-center mb-4">Você tem certeza que deseja remover <strong>{{ itemToRemove?.name || itemToRemove?.description }}</strong> do carrinho?</p>
          
          <div class="d-flex flex-row gap-3 mt-4"> 
            <button class="btn btn-outline-secondary w-100" @click="cancelRemove">
              <i class="fas fa-times me-2"></i>
              Cancelar
            </button>
            
            <button class="btn btn-color-site w-100" @click="confirmRemove">
              <i class="fa-solid fa-trash-can me-2"></i>
              Remover
            </button>
          </div>
        </div>
      </template>
    </BottomModal>
  </div>
</template>

<script>
import { useCartStore } from '@/stores/cartStore'
import { useRoute } from 'vue-router'
import { computed, ref, watch, onMounted } from 'vue'
import { useEventTheme } from '@/composables/useEventTheme'
import BottomModal from '@/components/BottomModal.vue'
import FullScreenLoader from '@/components/FullScreenLoader.vue'
import { formatCurrency } from '@/utils/currencyFormatter'
import CartHeader from '@/components/CartHeader.vue'

export default {
  name: 'CartPage',
  components: { BottomModal, CartHeader, FullScreenLoader },
  setup() {
    const cart = useCartStore()
    const route = useRoute()
    const eventId = route.params.id
    const { isReady: themeReady } = useEventTheme(eventId, { cleanup: true })

    const loading = ref(true)
    watch(themeReady, (ready) => {
      if (ready) setTimeout(() => { loading.value = false }, 200)
    }, { immediate: true })

    const items = computed(() => {
      const event = cart.getEvent(eventId)
      return event ? event.items : []
    })

    const event = computed(() => {
      const event = cart.getEvent(eventId)
      return event ? event : {}
    })

    const isEventDraft = computed(() => event.value?.status === 'draft')

    const total = computed(() =>
      items.value.reduce((acc, item) => acc + item.price * item.quantity, 0)
    )

    const childName = computed(() => route.query.childName || 'a criança')

    const showRemoveModal = ref(false)
    const itemToRemove = ref(null)

    function increase(item) {
      item.quantity += 1
      cart.saveToStorage()
    }

    function decrease(item) {
      if (item.quantity <= 1) {
        askRemoveItem(item)
        return
      }
      item.quantity -= 1
      cart.saveToStorage()
    }

    function askRemoveItem(item) {
      itemToRemove.value = item
      showRemoveModal.value = true
    }

    function cancelRemove() {
      itemToRemove.value = null
      showRemoveModal.value = false
    }

    function confirmRemove() {
      if (itemToRemove.value) {
        cart.removeItem(eventId, itemToRemove.value.id)
        cart.saveToStorage()
      }
      cancelRemove()
    }

    const goBack = () => {
      router.back();
    };

    return {
      goBack,
      eventId,
      loading,
      items,
      event,
      total,
      childName,
      isEventDraft,
      increase,
      decrease,
      askRemoveItem,
      confirmRemove,
      cancelRemove,
      showRemoveModal,
      itemToRemove,
      formatCurrency
    }
  }
}
</script>

<style scoped>
:root {
  --primary-color: #ff50a3;
  --secondary-color: #32355d;
  --text-muted-color: #6c757d;
  --border-color: #eee;
  --shadow-light: 0 2px 4px rgba(0, 0, 0, 0.08);
  --shadow-medium: 0 4px 8px rgba(0, 0, 0, 0.12);
  --border-radius-base: 0.75rem;
  --padding-base: 1rem;
  --sidebar-width: 270px; 
  --container-desktop-padding: 1.5rem; 
}

.page {
  display: flex;
  flex-direction: column;
  background-color: var(--event-bg, #f8f9fa);
  height: 100%;
  width: 100%;
  max-width: 750px;
  overflow-x: hidden;
  box-sizing: border-box;
}

.cart-content-wrapper {
  padding: 1rem;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: clip;
}

@media (max-width: 767.98px) {
  .cart-content-wrapper {    
    padding: 1rem;
    padding-bottom: 96px;
    min-width: 0;
  }
  .page { 
    height: calc(100% + 1rem);   
    margin-top: 1rem;
    min-width: 0;
  }
}

.empty-cart-message {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 2rem;
  margin-top: 3rem;
}

.empty-cart-message p {
  font-size: 0.98rem;
  max-width: 350px;
  line-height: 1.5;
}

.empty-state-link {
  color: var(--event-primary, #ff50a3);
  font-weight: 700;
  text-decoration: none;
  border-bottom: 1px solid var(--event-primary-shadow, rgba(255, 80, 163, 0.35));
  padding-bottom: 1px;
  transition: color 0.18s ease, border-color 0.18s ease;
}

.empty-state-link:hover {
  color: color-mix(in srgb, var(--event-primary, #ff50a3) 82%, #000);
  border-color: var(--event-primary-shadow, rgba(255, 80, 163, 0.35));
}

/* ── Cabeçalho da seção (igual GiftMasonry) ─────────────────── */
.gifts-section-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 1rem;
  gap: 0.5rem;
}
.gifts-title {
  font-size: 1.4rem;
  font-weight: 800;
  color: #32355d;
  margin: 0 0 0.2rem;
}
.gifts-subtitle {
  font-size: 0.85rem;
  color: #a0a5b8;
  margin: 0;
}
.gifts-count {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--event-primary, #ff50a3);
  background: var(--event-primary-light, #fff0f6);
  border: 1px solid color-mix(in srgb, var(--event-primary, #ff50a3) 25%, transparent);
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  white-space: nowrap;
  flex-shrink: 0;
}

/* ── Cards de item (igual GiftMasonry) ──────────────────────── */
.cart-items-list {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  overflow-x: clip;
}
.cart-item-card {
  background: #fff;
  border: 1px solid #f0f0f0 !important;
  transition: box-shadow 0.2s, transform 0.2s;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}
.cart-item-card:hover {
  box-shadow: 0 6px 20px rgba(0,0,0,0.10) !important;
  transform: translateY(-2px);
}
.cart-item-img-wrapper {
  width: 72px;
  height: 72px;
  min-width: 72px;
  border-radius: 0.75rem;
  overflow: hidden;
  background: #f8f8fa;
}
.cart-item-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.cart-item-row {
  min-width: 0;
}
.cart-item-details {
  min-width: 0;
  width: 0;
}
.cart-item-actions {
  min-width: 0;
}
.cart-item-title {
  color: #32355d;
  font-size: 0.98rem;
  font-weight: 700;
}
.cart-item-price {
  color: #32355d;
  font-size: 1.05rem;
}
.one-line {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── Controles de quantidade (pill rosa) ────────────────────── */
.cart-qty-pill {
  display: inline-flex;
  align-items: center;
  background: #f4f4f7;
  border-radius: 999px;
  border: 1px solid #e4e6ef;
  padding: 0.1rem 0.4rem;
  gap: 0.2rem;
}
.qty-btn {
  background: none;
  border: none;
  color: var(--event-primary, #ff50a3);
  font-size: 1.1rem;
  font-weight: 700;
  width: 1.7rem;
  height: 1.7rem;
  border-radius: 50%;
  transition: background 0.15s;
  cursor: pointer;
  line-height: 1;
}
.qty-btn:hover {
  background: var(--event-primary-light, #ffe4f3);
}
.qty-value {
  font-weight: 700;
  color: #32355d;
  min-width: 1.4rem;
  text-align: center;
  font-size: 0.95rem;
}

/* ── Botão remover ──────────────────────────────────────────── */
.btn-remove-item {
  background: none;
  border: none;
  color: #ccc;
  font-size: 1.1rem;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;
  cursor: pointer;
}
.btn-remove-item:hover {
  background: #ffeaea;
  color: #d32f2f;
}

.confirmation-content h5 {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--primary-color); 
}
.confirmation-content p {
  font-size: 0.95rem;
  color: var(--text-muted-color); 
}
.confirmation-content .btn {
  padding: 0.75rem 1.5rem; 
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
}

.app-footer {
  position: sticky;
  bottom: 0;
  width: 100%;
  z-index: 100;
  background: transparent;
  pointer-events: none;
}

.app-footer__inner {
  background: #fff;
  border-top: 1px solid #f0f0f0;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.07);
  padding: 0.75rem 1.25rem 1rem;
  pointer-events: all;
  max-width: 640px;
  margin: 0 auto;
  border-radius: 16px 16px 0 0;
}

.app-footer__actions {
  display: flex;
  gap: 10px;
}

.btn-footer-back {
  background: #fff;
  border: 1.5px solid #e0e0e0;
  color: #6c757d;
  border-radius: 0.75rem;
  padding: 0.65rem 1.25rem;
  font-size: 0.88rem;
  font-weight: 600;
  transition: all 0.18s;
  flex-shrink: 0;
}

.btn-footer-back:hover:not(:disabled) {
  border-color: #adb5bd;
  color: #32355d;
}

.btn-footer-next {
  flex: 1;
  background: var(--event-primary, #ff50a3);
  color: #fff !important;
  border: none;
  border-radius: 0.75rem;
  padding: 0.65rem 1.25rem;
  font-size: 0.92rem;
  font-weight: 700;
  transition: background 0.18s, transform 0.15s, box-shadow 0.18s;
}

.btn-footer-next--disabled {
  background: #c8c8d0 !important;
  cursor: not-allowed;
  opacity: 0.75;
}

.btn-footer-next--disabled:hover {
  background: #c8c8d0 !important;
  transform: none;
  box-shadow: none;
}

.draft-checkout-notice {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  margin-bottom: 0.65rem;
  padding: 0.55rem 0.75rem;
  background: #f7f7fb;
  border-radius: 0.5rem;
  font-size: 0.83rem;
  color: #5a6782;
  line-height: 1.45;
}

.draft-checkout-notice__icon {
  color: #bbb;
  margin-top: 0.1rem;
  flex-shrink: 0;
}

.draft-checkout-notice__link {
  color: var(--event-primary, #ff50a3);
  font-weight: 600;
  text-decoration: underline;
}

.btn-footer-next:hover {
  background: color-mix(in srgb, var(--event-primary, #ff50a3) 84%, #000);
  opacity: 1;
  transform: translateY(-1px);
  box-shadow: 0 4px 14px var(--event-primary-shadow, rgba(255, 80, 163, 0.35));
}

@media (max-width: 767.98px) {
  .app-footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    z-index: 1031;
  }

  .app-footer__inner {
    max-width: 100%;
    border-radius: 12px 12px 0 0;
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }

  .cart-item-row {
    gap: 0.6rem !important;
    padding: 0.75rem !important;
  }
  .cart-item-img-wrapper {
    width: 60px;
    height: 60px;
    min-width: 60px;
  }
  .cart-item-title {
    font-size: 0.92rem;
  }
  .cart-item-price {
    font-size: 0.96rem;
  }
  .cart-item-actions {
    gap: 0.4rem !important;
  }
  .cart-qty-pill {
    padding: 0.05rem 0.35rem;
  }
  .qty-btn {
    width: 1.55rem;
    height: 1.55rem;
  }
  .btn-footer-back,
  .btn-footer-next {
    padding: 0.6rem 0.9rem;
    font-size: 0.86rem;
  }
}

@media (min-width: 768px) {
  .app-footer {
    z-index: 99; 
  }
}
</style>